import SendClockIcon from "@components/icons/SendClockIcon";

// Chip styling per iCalendar STATUS (chips in the month/week grids).
export const statusChipStyles = {
  CONFIRMED: "bg-primary-50 text-primary-800 hover:bg-primary-100",
  TENTATIVE: "bg-yellow-50 text-yellow-800 hover:bg-yellow-100",
  "NEEDS-ACTION": "bg-yellow-50 text-yellow-800 hover:bg-yellow-100",
  CANCELLED: "bg-zinc-50 text-zinc-400 line-through hover:bg-zinc-100",
  COMPLETED: "bg-green-50 text-green-700 hover:bg-green-100",
  "IN-PROCESS": "bg-blue-50 text-blue-700 hover:bg-blue-100",
};

export const defaultChipStyle = "bg-zinc-100 text-zinc-700 hover:bg-zinc-200";

// Chat-created tasks get the same neutral slate identity as the activity feed, so a to-do reads the
// same wherever it appears. Completed/cancelled tasks keep their status style so "done"/"cancelled" reads.
export const taskChipStyle = "bg-slate-100 text-slate-700 hover:bg-slate-200";

// Badge colours per STATUS (details modal / agenda list).
export const statusBadgeColors = {
  CONFIRMED: "green",
  TENTATIVE: "yellow",
  "NEEDS-ACTION": "yellow",
  CANCELLED: "red",
  COMPLETED: "green",
  "IN-PROCESS": "blue",
};

export const statusLabels = {
  CONFIRMED: "Confirmed",
  TENTATIVE: "Tentative",
  "NEEDS-ACTION": "Needs action",
  CANCELLED: "Cancelled",
  COMPLETED: "Completed",
  "IN-PROCESS": "In progress",
};

export const formatStatus = (status) =>
  statusLabels[status] ??
  (status ? status.charAt(0) + status.slice(1).toLowerCase() : "");

// Grid placement date: events sit on their start, tasks on their due date.
export const itemAnchorDate = (item) => item.start ?? item.due ?? null;

// Owner colours for the shared "team view" overlay. Applied as an ADDITIVE left-border accent on the
// chip (via `borderClass`) plus a legend/checkbox swatch (`swatchClass`) — the STATUS/projection
// palette above is left untouched, so a chip still reads its own state and only gains a who-owns-it
// stripe. Class names are full literals so Tailwind's JIT keeps them.
//
// Colours are assigned by POSITION, not by a hash of the id — a hash collides (two users landing on
// the same colour), which defeats the whole point of telling calendars apart. Build one map from the
// ordered roster with `buildOwnerColorMap` and share it between the grid and the picker so every
// displayed calendar gets a distinct colour and the swatch always matches the stripe.
// swatchClass — legend dot; borderClass — the chip's left-border stripe on the grid; outlineClass +
// checkedBgClass — colour the picker checkbox (coloured outline always, coloured fill when ticked) so
// it stands in for a separate dot. All full literals so Tailwind's JIT keeps them.
export const ownerColorPalette = [
  { swatchClass: "bg-sky-500", borderClass: "border-l-sky-500", outlineClass: "border-sky-500", checkedBgClass: "checked:bg-sky-500" },
  { swatchClass: "bg-violet-500", borderClass: "border-l-violet-500", outlineClass: "border-violet-500", checkedBgClass: "checked:bg-violet-500" },
  { swatchClass: "bg-amber-500", borderClass: "border-l-amber-500", outlineClass: "border-amber-500", checkedBgClass: "checked:bg-amber-500" },
  { swatchClass: "bg-rose-500", borderClass: "border-l-rose-500", outlineClass: "border-rose-500", checkedBgClass: "checked:bg-rose-500" },
  { swatchClass: "bg-emerald-500", borderClass: "border-l-emerald-500", outlineClass: "border-emerald-500", checkedBgClass: "checked:bg-emerald-500" },
  { swatchClass: "bg-cyan-500", borderClass: "border-l-cyan-500", outlineClass: "border-cyan-500", checkedBgClass: "checked:bg-cyan-500" },
  { swatchClass: "bg-fuchsia-500", borderClass: "border-l-fuchsia-500", outlineClass: "border-fuchsia-500", checkedBgClass: "checked:bg-fuchsia-500" },
  { swatchClass: "bg-lime-500", borderClass: "border-l-lime-500", outlineClass: "border-lime-500", checkedBgClass: "checked:bg-lime-500" },
  { swatchClass: "bg-orange-500", borderClass: "border-l-orange-500", outlineClass: "border-orange-500", checkedBgClass: "checked:bg-orange-500" },
  { swatchClass: "bg-teal-500", borderClass: "border-l-teal-500", outlineClass: "border-teal-500", checkedBgClass: "checked:bg-teal-500" },
  { swatchClass: "bg-indigo-500", borderClass: "border-l-indigo-500", outlineClass: "border-indigo-500", checkedBgClass: "checked:bg-indigo-500" },
  { swatchClass: "bg-pink-500", borderClass: "border-l-pink-500", outlineClass: "border-pink-500", checkedBgClass: "checked:bg-pink-500" },
];

// Maps each owner id (in order) to a distinct palette entry. `orderedUserIds` should be a stable
// roster (e.g. [me, ...colleagues sorted by name]) so a colleague keeps the same colour as calendars
// are toggled. Distinct up to the palette size, then wraps — a fallback for teams larger than the
// palette rather than the common case.
export const buildOwnerColorMap = (orderedUserIds) => {
  const map = {};
  orderedUserIds.filter(Boolean).forEach((userId, index) => {
    map[userId] = ownerColorPalette[index % ownerColorPalette.length];
  });
  return map;
};

// Work Ontik scheduled on the merchant's behalf gets its own glyph, label and palette
// rather than the generic STATUS chip — these rows are the product about to act, and
// they need to be recognisable at a glance among the merchant's own diary. The glyph and
// the promise/indigo palettes match the corresponding row in the note timeline's activity
// feed, so the same work looks the same in both places. Keyed on the action type the feed
// declares; an unregistered type falls back to the plain STATUS chip.
// `chip` — the month-grid list item; `tile` — the icon square in the detail modal; `panel`
// — the Week view's grouped tile (a tinted card, no hover, per-line hover added by the tile).
export const projectionIdentities = {
  PromiseToPay: {
    label: "Promise to pay",
    heading: "Promise to pay",
    Icon: SendClockIcon,
    chip: "bg-promise-50 text-promise-700 hover:bg-promise-100",
    tile: "bg-promise-50 text-promise-600",
    panel: "bg-promise-50 text-promise-700",
  },
  // A merchant-scheduled payment request — the same single-send shape as a promise to pay, but
  // nothing the customer committed to. It reads as the send it is: the payment plan's send-clock
  // glyph with the payment-request orange, distinct from the promise (handshake/teal).
  ScheduledPaymentRequest: {
    label: "Scheduled payment request",
    heading: "Scheduled payment request",
    Icon: SendClockIcon,
    chip: "bg-primary-50 text-primary-700 hover:bg-primary-100",
    tile: "bg-primary-50 text-primary-600",
    panel: "bg-primary-50 text-primary-700",
  },
  PaymentPlan: {
    label: "Payment plan",
    heading: "Payment plan",
    Icon: SendClockIcon,
    chip: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    tile: "bg-indigo-50 text-indigo-600",
    panel: "bg-indigo-50 text-indigo-700",
  },
};

// A promise and a merchant-scheduled send carry the same actionType (PromiseToPay) on the
// projection — scheduleKind is the discriminator carried separately (see ics.js's
// X-ONTIK-SCHEDULE-KIND), so it takes priority over the actionType lookup.
export const projectionIdentity = (item) =>
  item?.projection?.scheduleKind === "schedule"
    ? projectionIdentities.ScheduledPaymentRequest
    : (projectionIdentities[item?.projection?.actionType] ?? null);

// "£300" when whole, "£4,273.33" otherwise. Shared by the Week tiles and the detail modal
// so a scheduled amount reads the same everywhere.
export const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const fractionDigits = Number.isInteger(number) ? 0 : 2;
  return `£${number.toLocaleString("en-GB", { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`;
};
